"use client";
import { useRef } from "react";
import Link from "next/link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { useScroll, useTransform, useReducedMotion, motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";

import Button from "@components/ui/form/button";
import { HeroProps } from "@src/types/section";
import { contentfulLoader } from "./image";
import { Speedline } from "@components/ui/motion";

const textVariants = {
	hidden: { opacity: 0, y: 30 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: {
			delay: i * 0.2,
			duration: 0.8,
			ease: [0.25, 0.4, 0.25, 1] as [number, number, number, number],
		},
	}),
};

const Hero = ({ section }: { section: HeroProps }) => {
	const ref = useRef(null);
	const shouldReduceMotion = useReducedMotion();
	// Track the hero's own scroll position (needs a target ref, otherwise the
	// offset is ignored and progress is measured against the whole page).
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start start", "end start"],
	});
	// Subtle percentage parallax on an oversized layer — GPU-composited, no gaps.
	const translate = useTransform(scrollYProgress, [0, 1], ["0%", shouldReduceMotion ? "0%" : "-25%"]);

	return (
		<div ref={ref} className="h-[45svh] lg:h-[80svh] relative py-16 lg:py-64 overflow-hidden w-full">
			{section?.heroVideo ? (
				<>
					<motion.video
						src={section?.heroVideo?.url}
						autoPlay
						loop
						muted
						playsInline
						preload="auto"
						className="absolute top-0 left-0 z-0 w-full h-[140%] object-cover"
						style={{
							y: translate,
							willChange: "transform",
						}}
					/>
					<div className="absolute inset-0 h-full w-full text-slate-50 bg-linear-to-b from-deep/70 via-transparent to-deep z-10 px-4 flex items-center">
						<div className="flex justify-center items-center text-center content-container">
							<div>
								<Speedline className="mb-4 mx-auto" width="6rem" />
								<motion.h1
									className="text-4xl sm:text-6xl lg:text-8xl font-bold leading-tight"
									custom={0}
									initial="hidden"
									animate="visible"
									variants={textVariants}
								>
									{section?.heroTitle}
								</motion.h1>

								{section?.heroText && (
									<motion.div
										className="max-w-2xl mx-auto text-center"
										custom={1}
										initial="hidden"
										animate="visible"
										variants={textVariants}
									>
										{documentToReactComponents(section?.heroText?.json)}
									</motion.div>
								)}

								{section?.heroCta && (
									<motion.div custom={2} initial="hidden" animate="visible" variants={textVariants}>
										<Link href={section?.heroCta?.url}>
											<Button className="mt-12 shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/50 transition-shadow duration-300">
												{section?.heroCta?.label}
											</Button>
										</Link>
									</motion.div>
								)}
							</div>
						</div>
					</div>
				</>
			) : (
				<>
					<motion.div
						className="absolute top-0 left-0 bg-cover bg-no-repeat bg-center z-0 w-full h-full"
						style={{
							y: translate,
							willChange: "transform",
							backgroundImage: `linear-gradient(180deg, rgba(15, 15, 15, 0.7) 0%, rgba(15, 15, 15, 0) 40%, rgba(15, 15, 15, 1) 90%), url(${contentfulLoader(
								{
									src: section?.heroImage?.url ?? "",
									width: 1920,
									quality: 75,
								}
							)})`,
							backgroundPositionX: "center",
							backgroundPositionY: "bottom",
						}}
					/>
					<div className="relative max-w-4xl text-center py-24 lg:py-0 lg:text-left mx-auto text-slate-50 z-10 px-4">
						<Speedline className="hidden lg:block mb-4" width="6rem" />
						<motion.h1
							className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight"
							custom={0}
							initial="hidden"
							animate="visible"
							variants={textVariants}
						>
							{section?.heroTitle}
						</motion.h1>

						{section?.heroText && (
							<motion.div custom={1} initial="hidden" animate="visible" variants={textVariants}>
								{documentToReactComponents(section?.heroText?.json)}
							</motion.div>
						)}

						{section?.heroCta && (
							<motion.div custom={2} initial="hidden" animate="visible" variants={textVariants}>
								<Link href={section?.heroCta?.url}>
									<Button className="mt-12 shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/50 transition-shadow duration-300">
										{section?.heroCta?.label}
									</Button>
								</Link>
							</motion.div>
						)}
					</div>
				</>
			)}

			{/* Scroll indicator */}
			{/* <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-99 animate-bounce">
				<FaChevronDown className="text-white/60 text-xl" />
			</div> */}
		</div>
	);
};

export default Hero;
